import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  styled,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getGrmOrderDetails } from "../../api/grm";
import { getScriptCoOrderDetails } from "../../api/scriptco";
import AscellaTracking from "./AscellaTracking";
import { capitalizeFirstLetter } from "../../utils/capitalize";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    width: "750px",
    maxWidth: "100%",
    borderRadius: "24px",
    padding: "24px",
    backgroundColor: "#FFFFFF",
    boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
  },
}));

const InfoField = ({ label, value }) => {
  const isDash = value === "-";
  return (
    <Box
      sx={{
        mb: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <Typography
        sx={{
          color: "#6B7280",
          fontSize: "12px",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: 0.5,
          textAlign: isDash ? "center" : "left",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#111827",
          fontSize: "15px",
          fontWeight: 600,
          wordBreak: "break-all",
          textAlign: isDash ? "center" : "left",
        }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  );
};

InfoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

const formatGRMTimestamp = (timestamp) => {
  if (!timestamp || timestamp === "--" || timestamp === "N/A") return "N/A";
  try {
    const str = String(timestamp).trim();
    if (!str) return "N/A";

    if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(str)) {
      return str;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [y, m, d] = str.split("-");
      return `${m.padStart(2, "0")}/${d.padStart(2, "0")}/${y}`;
    }

    let dateObj;
    if (/^\d{10,13}$/.test(str)) {
      dateObj = new Date(Number(str));
    } else {
      let fixedStr = str.replace(/(\.\d{3})\d+$/, "$1");
      if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(fixedStr) && fixedStr.includes("T")) {
        fixedStr += "Z";
      } else if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(fixedStr) && fixedStr.includes(" ")) {
        fixedStr = fixedStr.replace(" ", "T") + "Z";
      }
      dateObj = new Date(fixedStr);
    }

    if (dateObj && !Number.isNaN(dateObj.getTime())) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const dateStr = dateObj.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        timeZone: tz,
      });
      const timeStr = dateObj.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: tz,
      });
      return `${dateStr} ${timeStr}`;
    }

    return str;
  } catch (e) {
    console.error("Error formatting GRM timestamp:", e);
    return String(timestamp);
  }
};

const formatOptionalTimestamp = (value) =>
  value ? formatGRMTimestamp(value) : "N/A";

const isScriptCoOrder = (order) => {
  const vendorStr = String(
    order.vendor || order.vendorName || order.programName || "",
  ).toLowerCase();
  return (
    vendorStr.includes("scriptco") ||
    vendorStr.includes("script co") ||
    String(order.orderId || "")
      .toLowerCase()
      .includes("scriptco")
  );
};

const getCleanOrderId = (order) =>
  order.orderId
    ? String(order.orderId)
        .replaceAll("#", "")
        .replaceAll(/GRM|SCRIPTCO/gi, "")
        .trim()
    : "";

const getItemFullName = (item) => {
  const firstName =
    item?.customerDetails?.firstName ||
    item?.firstName ||
    item?.PatientFirstName ||
    "";
  const lastName =
    item?.customerDetails?.lastName ||
    item?.lastName ||
    item?.PatientLastName ||
    "";
  return `${firstName} ${lastName}`.trim();
};

const buildOrderFromExisting = (order, vendorName, isScriptCo, memberName) => ({
  ...order,
  orderId: order.orderId || "N/A",
  trackingId: order.trackingId || "N/A",
  vendor: vendorName,
  submittedOn: formatOptionalTimestamp(order.submittedOn),
  shippedDate: formatOptionalTimestamp(order.shippedDate),
  status: order.status || "Submitted",
  trackingHistory: order.trackingHistory || [],
  memberName,
  isScriptCo,
});

const buildScriptCoOrder = (item, order, vendorName) => {
  const fullName = getItemFullName(item);

  const rawSubmittedDate =
    item?.submittedOn ||
    item?.createdOrderPayload?.OrderDate ||
    item?.dateCreated ||
    item?.orderDate ||
    item?.createdDate ||
    item?.DateCreated ||
    order.submittedOn;

  const rawShippedDate =
    item?.trackingData?.[0]?.shippedDate ||
    item?.shippedDate ||
    item?.shippingDate ||
    order.shippedDate;

  const rawStatus =
    (typeof item?.orderStatus === "string"
      ? item.orderStatus
      : item?.orderStatus?.orderStatus) ||
    item?.status ||
    item?.vendorStatus ||
    item?.items?.[0]?.status ||
    item?.Status ||
    order.status ||
    "Submitted";

  const orderStatusVal =
    typeof rawStatus === "string"
      ? capitalizeFirstLetter(rawStatus)
      : "Submitted";

  const trackingNumVal =
    item?.trackingData?.[0]?.trackingNumber ||
    item?.trackingNumber ||
    item?.trackingNum ||
    item?.trackingId ||
    item?.items?.[0]?.shippingTrackingNumber ||
    item?.TrackingNumber ||
    order.trackingId ||
    "N/A";

  const rawOrderId =
    item?.orderNumber || item?.orderId || item?.id || order.orderId || "";
  const cleanOrderId = String(rawOrderId).replaceAll("#", "").trim();
  const displayOrderId = cleanOrderId ? `#${cleanOrderId}` : "N/A";

  return {
    orderId: displayOrderId,
    trackingId: trackingNumVal || "N/A",
    vendor: vendorName,
    submittedOn: formatOptionalTimestamp(rawSubmittedDate),
    shippedDate: formatOptionalTimestamp(rawShippedDate),
    status: orderStatusVal,
    trackingHistory: item?.trackingHistory || order.trackingHistory || [],
    memberName:
      capitalizeFirstLetter(fullName) ||
      capitalizeFirstLetter(order.memberName) ||
      "N/A",
    isScriptCo: true,
  };
};

const buildGrmOrder = (item, order, vendorName) => {
  const fullName = getItemFullName(item);

  const rawSubmittedDate =
    item?.submittedOn ||
    item?.createdOrderPayload?.OrderDate ||
    item?.dateCreated ||
    item?.orderDate ||
    item?.createdDate ||
    item?.DateCreated ||
    item?.date_created ||
    item?.created_at ||
    item?.createdAt ||
    item?.submittedDate ||
    order.submittedOn;

  const rawShippedDate =
    item?.trackingData?.[0]?.shippedDate ||
    item?.shippedDate ||
    item?.shippingDate ||
    order.shippedDate;

  const orderStatusVal =
    item?.orderStatus ||
    item?.status ||
    item?.items?.[0]?.status ||
    item?.Status ||
    order.status ||
    "Submitted";

  const trackingNumVal =
    item?.trackingNumber ||
    item?.trackingId ||
    item?.items?.[0]?.shippingTrackingNumber ||
    item?.TrackingNumber ||
    order.trackingId ||
    "N/A";

  const itemOrderId = item?.orderNumber || item?.orderId || item?.id;
  const displayOrderId = itemOrderId
    ? `#${itemOrderId}`
    : order.orderId || "N/A";

  return {
    orderId: displayOrderId,
    trackingId: trackingNumVal,
    vendor: vendorName,
    submittedOn: formatOptionalTimestamp(rawSubmittedDate),
    shippedDate: formatOptionalTimestamp(rawShippedDate),
    status: orderStatusVal,
    trackingHistory: item?.trackingHistory || order.trackingHistory || [],
    memberName: fullName || order.memberName || "N/A",
    isScriptCo: false,
  };
};

const fetchTrackedOrder = async (order) => {
  const isScriptCo = isScriptCoOrder(order);
  const cleanId = getCleanOrderId(order);
  const vendorName = order.vendor || (isScriptCo ? "ScriptCo" : "GRM");

  if (!cleanId) {
    return buildOrderFromExisting(
      order,
      vendorName,
      isScriptCo,
      capitalizeFirstLetter(order.memberName) || "N/A",
    );
  }

  try {
    const response = isScriptCo
      ? await getScriptCoOrderDetails(cleanId)
      : await getGrmOrderDetails(cleanId);

    if (response && !response.error) {
      const rawItem = response.result || response.data || response;
      const item = Array.isArray(rawItem) ? rawItem[0] : rawItem;

      return isScriptCo
        ? buildScriptCoOrder(item, order, vendorName)
        : buildGrmOrder(item, order, vendorName);
    }
  } catch (err) {
    console.error(`Failed to fetch details for order ${cleanId}:`, err);
  }

  return buildOrderFromExisting(
    order,
    vendorName,
    isScriptCo,
    order.memberName || "N/A",
  );
};

const TrackEnrollmentModal = ({ open, onClose, orders, isAscella }) => {
  const [trackData, setTrackData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (isAscella) {
        setTrackData([]);
        return;
      }
      const fetchOrderHistory = async () => {
        setLoading(true);
        try {
          if (!orders || orders.length === 0) {
            setTrackData([]);
            return;
          }

          const fetchPromises = orders.map((order) => fetchTrackedOrder(order));

          const results = await Promise.all(fetchPromises);
          setTrackData(results);
        } catch (error) {
          console.error("Failed to fetch order history for tracking:", error);
          setTrackData([]);
        } finally {
          setLoading(false);
        }
      };
      void fetchOrderHistory();
    }
  }, [open, orders, isAscella]);

  // GRM Stages as requested:
  // Today - submitted
  // Tomorrow - in review
  // Day after - Correction in progress
  // Day after - documents needed
  // Day after - in review
  // Day after - approved
  const displayOrders = trackData.length > 0 ? trackData : orders || [];

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            py: 8,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress sx={{ color: "#403C6F" }} />
        </Box>
      );
    }
    if (!displayOrders || displayOrders.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ color: "#6B7280", fontStyle: "italic" }}>
            No orders found to track.
          </Typography>
        </Box>
      );
    }
    return (
      <Box>
        {displayOrders.map((order, orderIndex) => {
          if (isAscella) {
            return (
              <AscellaTracking
                key={order.orderId || orderIndex}
                order={order}
              />
            );
          }

          const vendorStr = String(
            order.vendor || order.vendorName || order.programName || "",
          ).toLowerCase();
          const isScriptCoOrder =
            order.isScriptCo ||
            vendorStr.includes("scriptco") ||
            vendorStr.includes("script co") ||
            String(order.orderId || "")
              .toLowerCase()
              .includes("scriptco");

          return (
            <Box
              key={order.orderId || orderIndex}
              sx={{
                mb: orderIndex === displayOrders.length - 1 ? 0 : 8,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#F9FAFB",
                  p: 3,
                  borderRadius: "20px",
                  mb: 4,
                  border: "1px solid #F3F4F6",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box sx={{ width: "calc(33.33% - 16px)" }}>
                    <InfoField label="Order ID" value={order.orderId} />
                  </Box>
                  <Box sx={{ width: "calc(33.33% - 16px)" }}>
                    <InfoField label="Tracking ID" value={order.trackingId} />
                  </Box>
                  <Box sx={{ width: "calc(33.33% - 16px)" }}>
                    <InfoField label="Status" value={order.status} />
                  </Box>
                  {!isScriptCoOrder && (
                    <Box sx={{ width: "calc(33.33% - 16px)" }}>
                      <InfoField label="Member Name" value={order.memberName} />
                    </Box>
                  )}
                  <Box sx={{ width: "calc(33.33% - 16px)" }}>
                    <InfoField label="Vendor" value={order.vendor} />
                  </Box>
                  <Box sx={{ width: "calc(33.33% - 16px)" }}>
                    <InfoField label="Submitted on" value={order.submittedOn} />
                  </Box>
                  {isScriptCoOrder && (
                    <Box sx={{ width: "calc(33.33% - 16px)" }}>
                      <InfoField
                        label="Shipped Date"
                        value={order.shippedDate}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          m: 0,
          p: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "24px",
            color: "#111827",
            letterSpacing: "-0.02em",
          }}
        >
          Track Enrollment
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "#6B7280",
            backgroundColor: "#F3F4F6",
            "&:hover": {
              backgroundColor: "#E5E7EB",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflowY: "auto", maxHeight: "75vh" }}>
        {renderContent()}
      </DialogContent>
    </StyledDialog>
  );
};

TrackEnrollmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isAscella: PropTypes.bool,
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      orderId: PropTypes.string,
      trackingId: PropTypes.string,
      enrollmentId: PropTypes.string,
      memberName: PropTypes.string,
      vendor: PropTypes.string,
      submittedOn: PropTypes.string,
      shippedDate: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
};

export default TrackEnrollmentModal;
